import { useCallback, useEffect, useRef, useState } from "react";
import { useTenantStore } from "@/stores/tenant-store";

export function bankingScope() {
  const state = useTenantStore.getState();
  const organization =
    state.activeOrganizationId &&
    state.organizations[state.activeOrganizationId];
  return state.activeTenantId &&
    organization &&
    organization.tenantId === state.activeTenantId
    ? `${state.activeTenantId}:${organization.id}`
    : "";
}

/** Keyed page/account children own requests. Ignore even non-abortable late responses. */
export function useBankingRequest() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const scope = useRef(bankingScope());
  const mounted = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const sequence = useRef(0);
  useEffect(() => {
    mounted.current = true;
    const unsubscribe = useTenantStore.subscribe(() => {
      if (scope.current !== bankingScope()) {
        sequence.current++;
        controller.current?.abort();
      }
    });
    return () => {
      mounted.current = false;
      controller.current?.abort();
      unsubscribe();
    };
  }, []);
  const run = useCallback(
    async <T>(
      request: (signal: AbortSignal) => Promise<T>,
      apply: (value: T) => void,
      allowed = true,
    ) => {
      if (
        !allowed ||
        !mounted.current ||
        !scope.current ||
        scope.current !== bankingScope()
      )
        return;
      controller.current?.abort();
      const current = ++sequence.current;
      const next = new AbortController();
      controller.current = next;
      const live = () =>
        mounted.current &&
        !next.signal.aborted &&
        current === sequence.current &&
        scope.current === bankingScope();
      setPending(true);
      setError(null);
      try {
        const value = await request(next.signal);
        if (live()) apply(value);
      } catch (cause) {
        if (live())
          setError(
            cause instanceof Error
              ? cause
              : new Error("No se pudo completar la solicitud."),
          );
      } finally {
        if (live()) setPending(false);
      }
    },
    [],
  );
  return { pending, error, run };
}
